import { ReactNode } from "react";
import { FieldLabel, FieldLabelProps } from "../../../../components/FieldLabel";
import { SrOnly } from "../../../../components/SrOnly";

type GetLabelProps = (options?: object) => { id: string; htmlFor: string };

/**
 * Props for the SelectFieldLabel component
 * @property {AiMarkWithTooltipOrPopoverProps} aiMark - The AI mark to display
 * @property {string} label - The text content to display as the label
 * @property {GetLabelProps} getLabelProps - Downshift getter function that returns accessibility props for the label
 * @property {boolean} [required] - Whether the field is required. Shows a red asterisk (*) when true.
 * @property {ReactNode} [moreInfo] - Additional information to display in a tooltip
 * @property {boolean} [moreInfoOpen] - Controls the open state of the more info tooltip
 * @property {boolean} [hideLabel] - Visually hides the label while keeping it accessible to screen readers
 */
export type SelectFieldLabelProps = {
  aiMark?: FieldLabelProps["aiMark"];
  label: string;
  getLabelProps: GetLabelProps;
  hideLabel?: boolean;
  moreInfo?: ReactNode;
  moreInfoOpen?: boolean;
  required?: boolean;
};

/**
 * Internal label component for the SelectField that renders an accessible label element.
 *
 * Features:
 * - Renders a FieldLabel with proper accessibility attributes from Downshift
 * - Automatically associates the label with the combobox input
 * - Supports screen readers with proper labeling
 * - Optional required field indicator with red asterisk
 * - Help tooltip with info icon for additional context
 * - Can visually hide the label while maintaining accessibility
 *
 * @example
 * <SelectFieldLabel
 *   label="Select an option"
 *   getLabelProps={getLabelProps}
 *   required
 *   moreInfo="Choose from available options"
 * />
 */
export const SelectFieldLabel = ({
  aiMark,
  label,
  getLabelProps,
  hideLabel = false,
  moreInfo,
  moreInfoOpen,
  required,
}: SelectFieldLabelProps) => {
  const labelContent = (
    <FieldLabel
      {...getLabelProps()}
      aiMark={aiMark}
      required={required}
      moreInfo={moreInfo}
      moreInfoOpen={moreInfoOpen}
    >
      {label}
    </FieldLabel>
  );

  if (hideLabel) {
    return <SrOnly>{labelContent}</SrOnly>;
  }

  return labelContent;
};
